/**
 * Badge.jsx — v1.0.0 Gate 5 Item 2
 *
 * Unified badge visual language across the entire app.
 * Three sizes only:
 *   - mini      — 16px (inline text, nav indicators)
 *   - standard  — 32px (offer cards, activity feed)
 *   - feature   — 64px (badge detail, achievement moments)
 *
 * Props:
 *   badge: { badge_id, name, description, icon, earned, earned_at }
 *   size: "mini" | "standard" | "feature"
 *   locked: boolean — when true, renders silhouette (grayscale + low opacity)
 */
import {
  Award, Star, Crown, Trophy, Sparkles, Cake, Gift, Bookmark,
  BookmarkCheck, Flag, Map as MapIcon, UserCheck, AlertCircle,
  Calendar, CalendarCheck, Eye, Utensils, Users, Share2, Bell, Heart,
  RefreshCw, Sunrise, Moon, PartyPopper, Cog, BarChart, ClipboardCheck,
  BadgeCheck,
} from "lucide-react";

const ICON_MAP = {
  "award": Award, "star": Star, "crown": Crown, "trophy": Trophy,
  "sparkles": Sparkles, "cake": Cake, "gift": Gift, "bookmark": Bookmark,
  "bookmark-check": BookmarkCheck, "flag": Flag, "map": MapIcon,
  "user-check": UserCheck, "alert-circle": AlertCircle, "calendar": Calendar,
  "calendar-check": CalendarCheck, "eye": Eye, "utensils": Utensils,
  "users": Users, "share-2": Share2, "bell": Bell, "heart": Heart,
  "refresh-cw": RefreshCw, "sunrise": Sunrise, "moon": Moon,
  "party-popper": PartyPopper, "cog": Cog, "bar-chart": BarChart,
  "clipboard-check": ClipboardCheck, "badge-check": BadgeCheck,
};

const SIZE_CONFIG = {
  mini:     { wrap: 16, icon: 10, ring: 1,   bg: "bg-amber-400",  locked: "bg-slate-200" },
  standard: { wrap: 32, icon: 18, ring: 1.5, bg: "bg-amber-400",  locked: "bg-slate-200" },
  feature:  { wrap: 64, icon: 32, ring: 2,   bg: "bg-amber-400",  locked: "bg-slate-200" },
};

export function Badge({ badge, size = "standard", locked: lockedProp, className = "", onClick }) {
  const cfg = SIZE_CONFIG[size] || SIZE_CONFIG.standard;
  const isLocked = lockedProp !== undefined ? lockedProp : badge && badge.earned === false;
  const Icon = ICON_MAP[badge?.icon] || ICON_MAP[badge?.badge_icon] || Award;
  const wrapStyle = { width: cfg.wrap, height: cfg.wrap };

  const bgClass = isLocked ? cfg.locked : cfg.bg;
  const iconColor = isLocked ? "text-slate-400" : "text-white";
  const lockedStyle = isLocked
    ? { filter: "grayscale(1)", opacity: 0.55 }
    : {};

  const wrap = (
    <div
      className={`rounded-full flex items-center justify-center ${bgClass} ${className}`}
      style={{ ...wrapStyle, ...lockedStyle }}
      data-testid={`badge-${badge?.badge_id || "generic"}-${size}`}
      title={badge?.name || badge?.badge_name || ""}
    >
      <Icon className={iconColor} style={{ width: cfg.icon, height: cfg.icon }} strokeWidth={2.25} />
    </div>
  );

  if (!onClick) return wrap;
  return (
    <button
      type="button"
      onClick={onClick}
      className="transition-transform hover:scale-105 active:scale-95"
      aria-label={badge?.name || badge?.badge_name || "badge"}
    >
      {wrap}
    </button>
  );
}

export default Badge;

/**
 * BadgeCell — standard visual for a badge in a grid, shows name + status below.
 */
export function BadgeCell({ badge, onClick }) {
  const isLocked = badge.earned === false;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-slate-50 transition-colors w-full"
      data-testid={`badge-cell-${badge.badge_id}`}
    >
      <Badge badge={badge} size="standard" />
      <p className={`text-[10px] font-semibold text-center leading-tight line-clamp-2 ${
        isLocked ? "text-slate-400" : "text-slate-700"
      }`}>
        {badge.name}
      </p>
    </button>
  );
}
